<template>
    <div class="carousel-review">
        <div class="carousel">
            <div
                ref="container"
                :class="classSlides"
                class="slides"
            >
                <div
                    v-for="(
                        { date, review, starsCount, author }, index
                    ) in slides"
                    :key="index"
                    ref="elements"
                    class="slide"
                >
                    <CardReview
                        :author="author"
                        :date="date"
                        :review="review"
                        :stars-count="starsCount"
                    />
                </div>
            </div>
        </div>

        <ButtonRound
            :disabled="isAtStart"
            class="button-round left"
            :aria-label="t('components.text_carousel.button_prev')"
            icon="angle-left"
            @click="previous"
        />

        <ButtonRound
            :disabled="isAtEnd"
            :aria-label="t('components.text_carousel.button_next')"
            class="button-round right"
            icon="angle-right"
            @click="next"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { useCarousel } from '../base/useCarouselNew'
import { useIsMobile } from '@/composables/app/useIsMobile'

import type { Review } from '@/components/layout/carousels/review/CardReview.vue'
import CardReview from '@/components/layout/carousels/review/CardReview.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'
import { useI18n } from 'vue-i18n'

const { slides } = defineProps<{
    slides: Review[]
}>()

const { t } = useI18n()

// center the slide when only one slide shown at a time (mobile)
const { isMobile } = useIsMobile(640)
const { previous, next, container, slideWidth, elements, isAtStart, isAtEnd } =
    useCarousel(
        toRef(() => slides),
        isMobile
    )

const classSlides = computed(() => ({
    // center the slides on desktop if not enough slides to
    // fill the container
    centered:
        elements.value.length * slideWidth.value <
        (container.value?.offsetWidth || 0),
}))
</script>

<style lang="scss" scoped>
.carousel-review {
    position: relative;
    margin: auto;
    max-width: 1440px;
    display: flex;
    justify-content: center;

    &::before {
        position: absolute;
        content: '';
        top: 20px;
        left: 0;
        width: 100%;
        max-width: 420px;
        height: 300px;
        background: url('@/assets/icons/quotation-mark-green.svg') no-repeat;
        background-position: -120px 0;

        @include min-width(1280) {
            background-position: initial;
        }
    }

    .carousel {
        width: 100%;
        height: 396px;

        .slides {
            position: relative;
            // width: 100%;
            display: flex;
            overflow-y: hidden;
            overflow-x: scroll;
            scroll-snap-type: x mandatory;
            -ms-overflow-style: none; /* IE and Edge */
            scrollbar-width: none; /* Firefox */
            padding: 0 40px;
            scroll-padding: 0 40px;

            &.centered {
                justify-content: center;
            }

            &::after {
                content: '';
                padding-right: 40px;

                @include min-width(1024) {
                    padding-right: 0;
                }
            }

            @include min-width(1360) {
                padding: 0;
                scroll-padding: 0;
            }

            &::-webkit-scrollbar {
                display: none;
            }

            .slide {
                position: relative;
                user-select: none;
                scroll-snap-align: center;
                flex: 1 0 100%;
                margin-right: 5px;

                @include min-width(640) {
                    flex: 1 0 50%;
                    scroll-snap-align: start;
                }

                @include min-width(1024) {
                    flex: 1 0 33.33%;
                }

                @include min-width(1140) {
                    max-width: 340px;
                    flex: 1 0 25%;
                    margin: 0;
                }

                @include min-width(1360) {
                    max-width: 360px;
                    scroll-snap-align: start;

                    .card-review {
                        margin-right: -1px;
                    }
                }

                @include min-width(1440) {
                    flex: 1 0 25%;
                }
            }
        }
    }

    .button-round {
        position: absolute;
        z-index: 3;
        top: calc(50% - 25px);

        &.disabled {
            display: none;
        }

        &.left {
            left: 10px;
        }

        &.right {
            right: 15px;
        }

        @include min-width(1440) {
            &.left {
                left: -20px;
            }

            &.right {
                right: -20px;
            }
        }
    }
}
</style>
